import { useRef, useState } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api, { getAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import { fmtDateTime } from '../lib/format.js';

const ICON_FOR_MIME = (mime) => {
  if (!mime) return 'tabler:file';
  if (mime === 'application/pdf') return 'tabler:file-type-pdf';
  if (mime.startsWith('image/')) return 'tabler:photo';
  return 'tabler:file';
};

function fmtSize(bytes) {
  if (!bytes) return '—';
  const u = ['B', 'KB', 'MB', 'GB'];
  let n = bytes, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

/**
 * Reusable attachment list + uploader.
 *   parentType: 'invoice' | 'expense' | 'customer' | 'payment'
 *   parentId:   uuid
 *   items:      pre-loaded attachment array (from the parent's GET response)
 *   onChange:   () => void   — called after successful upload/delete so the parent re-fetches
 */
export default function AttachmentList({ parentType, parentId, items = [], onChange, compact = false }) {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileInput = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const canUpload = auth.can('attachments', 'upload');
  const canDelete = auth.can('attachments', 'delete');

  const uploadEndpoint =
    parentType === 'invoice'  ? `/invoices/${parentId}/attachments`  :
    parentType === 'expense'  ? `/expenses/${parentId}/attachments`  :
    parentType === 'customer' ? `/customers/${parentId}/attachments` :
    null;

  const upload = async (file) => {
    if (!file) return;
    if (!uploadEndpoint) { setError('Upload für diesen Typ nicht unterstützt.'); return; }
    setError(null); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(uploadEndpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (onChange) onChange();
    } catch (e) {
      setError(e.response?.data?.message || JSON.stringify(e.response?.data || e.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Anhang wirklich löschen?')) return;
    setError(null);
    try {
      await api.delete(`/attachments/${id}`);
      if (onChange) onChange();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const downloadUrl = (a) => {
    // Browser-native fetch with Bearer header doesn't work for <a download>;
    // open in new tab uses cookie auth which we don't have for /attachments.
    // Easiest: use api.get to fetch as blob, create object URL, click.
    return null; // unused — see openBlob below
  };

  const openBlob = async (a, asDownload = false) => {
    try {
      const r = await api.get(`/attachments/${a.id}/blob`, {
        params: asDownload ? { download: 1 } : undefined,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(r.data);
      if (asDownload) {
        const link = document.createElement('a');
        link.href = url;
        link.download = a.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (canUpload && e.dataTransfer.files.length) upload(e.dataTransfer.files[0]);
  };

  return (
    <div>
      {error && <Alert variant="danger" className="py-2 small mb-2">{error}</Alert>}

      {canUpload && uploadEndpoint && (
        <div
          className={`upload-dropzone ${dragOver ? 'is-dragging' : ''} ${busy ? 'is-busy' : ''}`}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}>
          <Icon icon="tabler:cloud-upload" style={{ fontSize: 28 }} />
          <div className="small">
            {busy ? 'Lade hoch…' : 'Datei hier ablegen oder klicken zum Auswählen'}
            <div className="text-muted">PDF, PNG, JPEG · max. 25 MB</div>
          </div>
          <input ref={fileInput} type="file" hidden
            accept="application/pdf,image/png,image/jpeg,image/webp,image/gif,image/tiff"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
        </div>
      )}

      {items.length === 0 ? (
        !canUpload && <div className="text-muted small">Keine Anhänge.</div>
      ) : (
        <div className="attachment-list mt-2">
          {items.map(a => (
            <div key={a.id} className="attachment-row">
              <Icon icon={ICON_FOR_MIME(a.mime_type)} className="attachment-icon" />
              <div className="flex-grow-1 min-w-0">
                <div className="attachment-name">{a.filename}</div>
                {!compact && (
                  <div className="text-muted small">
                    {fmtSize(a.size_bytes)} · {fmtDateTime(a.uploaded_at)}
                  </div>
                )}
              </div>
              <div className="d-flex gap-1">
                <Button size="sm" variant="outline-secondary" title="Anzeigen" onClick={() => openBlob(a, false)}>
                  <Icon icon="tabler:eye" />
                </Button>
                <Button size="sm" variant="outline-secondary" title="Herunterladen" onClick={() => openBlob(a, true)}>
                  <Icon icon="tabler:download" />
                </Button>
                {canDelete && (
                  <Button size="sm" variant="outline-danger" title="Löschen" onClick={() => remove(a.id)}>
                    <Icon icon="tabler:trash" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
